import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Bot, X, Minus, Plus, History } from "lucide-react";
import { api } from "@/lib/api";
import ChatView from "@/components/assistant/ChatView";
import { moduleForPath } from "@/components/assistant/moduleMap";

export default function FlowWidget() {
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [bootstrap, setBootstrap] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [unread, setUnread] = useState(false);

  useEffect(() => {
    api.get("/assistant/bootstrap").then((r) => setBootstrap(r.data)).catch(() => setBootstrap(null));
  }, []);

  // Hide on the Assistant Center itself
  if (location.pathname.startsWith("/dashboard/assistant")) return null;
  if (!bootstrap || !bootstrap.enabled || !bootstrap.plan_allowed) return null;

  const module = moduleForPath(location.pathname);

  return (
    <>
      {open && (
        <div
          className="fixed z-50 bg-bg-surface border border-line shadow-2xl flex flex-col
                     inset-0 rounded-none
                     sm:inset-auto sm:bottom-24 sm:right-5 sm:w-[400px] sm:h-[600px] sm:max-h-[calc(100vh-120px)] sm:rounded-2xl"
          role="dialog" aria-label="BroadcastFlow Assistant chat"
          data-testid="flow-panel"
        >
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-line bg-bg-elevated/60 sm:rounded-t-2xl">
            <div className="h-8 w-8 rounded-full bg-brand-amber/15 border border-brand-amber/40 flex items-center justify-center">
              <Bot className="h-4 w-4 text-brand-amber" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-ink-primary leading-tight">BroadcastFlow Assistant</div>
              <div className="text-[11px] text-ink-muted leading-tight">Flow{bootstrap.maintenance ? " · maintenance" : " · online"}</div>
            </div>
            <button onClick={() => setConversationId(null)} className="p-1.5 rounded-md text-ink-muted hover:text-ink-primary transition" title="New conversation" aria-label="New conversation" data-testid="flow-new-conversation">
              <Plus className="h-4 w-4" />
            </button>
            <button onClick={() => navigate("/dashboard/assistant")} className="p-1.5 rounded-md text-ink-muted hover:text-ink-primary transition" title="Conversation history" aria-label="Conversation history" data-testid="flow-history-btn">
              <History className="h-4 w-4" />
            </button>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-md text-ink-muted hover:text-ink-primary transition" title="Minimize" aria-label="Minimize assistant" data-testid="flow-minimize-btn">
              <Minus className="h-4 w-4" />
            </button>
            <button onClick={() => { setOpen(false); setConversationId(null); }} className="p-1.5 rounded-md text-ink-muted hover:text-red-400 transition" title="Close" aria-label="Close assistant" data-testid="flow-close-btn">
              <X className="h-4 w-4" />
            </button>
          </div>
          <ChatView
            conversationId={conversationId}
            onConversationChange={setConversationId}
            module={module}
            bootstrap={bootstrap}
            compact
          />
        </div>
      )}

      <button
        onClick={() => { setOpen((o) => !o); setUnread(false); }}
        className="fixed bottom-5 right-5 z-40 h-13 w-13 p-3.5 rounded-full bg-brand-amber text-black shadow-lg shadow-brand-amber/25 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-brand-amber/60 focus:ring-offset-2 focus:ring-offset-bg transition-transform"
        aria-label="Open BroadcastFlow Assistant"
        title="BroadcastFlow Assistant (Flow)"
        data-testid="flow-launcher-btn"
      >
        <Bot className="h-6 w-6" />
        {unread && <span className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-red-500 border-2 border-bg" data-testid="flow-unread-dot" />}
      </button>
    </>
  );
}
